'use client';

import * as React from 'react';
import { cx } from '@/lib/ui';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'success'
  | 'danger';

export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
};

const base =
  'inline-flex items-center justify-center gap-2 whitespace-nowrap font-semibold transition-colors ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aula-primary/35 focus-visible:ring-offset-2 ' +
  'disabled:pointer-events-none disabled:opacity-50';

const variants: Record<ButtonVariant, string> = {
  primary:
    'bg-aula-primary text-white shadow-aula-control hover:bg-aula-primary-strong',
  secondary:
    'border border-aula-border bg-white text-slate-800 shadow-aula-control hover:bg-slate-50',
  ghost: 'text-slate-700 hover:bg-slate-100 hover:text-slate-950',
  success:
    'bg-aula-success text-white shadow-aula-control hover:bg-aula-success-strong',
  danger:
    'bg-aula-danger text-white shadow-aula-control hover:bg-aula-danger-strong',
};

const sizes: Record<ButtonSize, string> = {
  sm: 'h-8 rounded-aula-sm px-3 text-xs',
  md: 'h-10 rounded-aula-md px-4 text-sm',
  lg: 'h-11 rounded-aula-md px-5 text-sm',
  icon: 'h-10 w-10 rounded-aula-md p-0',
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      loading = false,
      disabled,
      children,
      type = 'button',
      ...props
    },
    ref
  ) => (
    <button
      ref={ref}
      type={type}
      className={cx(base, variants[variant], sizes[size], className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? (
        <span
          className="h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent"
          aria-hidden="true"
        />
      ) : null}
      {children}
    </button>
  )
);

Button.displayName = 'Button';

export default Button;
